import appStore from '@stores/appStore'

import { isClientContactMasked } from './clientFieldAccess'

/**
 * Shown when an offshore staff member tries to preview a template they are not allowed to see.
 * Explains the reason so the block does not read as a fault. Mirrors the message the API returns
 * if the request gets through anyway.
 */
export const OFFSHORE_TEMPLATE_PREVIEW_DENIED_MESSAGE =
  'You are not authorised to use this function due to data privacy restriction'

/**
 * Email templates the current user may see, as an allowlist.
 *
 * Offshore staff have no access to correspondence by default — a template has to be named on the
 * list to become visible. Everyone else is unrestricted.
 */
function allowedTemplateKeys(): string[] {
  return appStore.offshoreVisibleTemplateKeys || []
}

/**
 * True when the allowlist applies to the current user at all. Everything below is a no-op for
 * anyone it does not apply to.
 *
 * Rides the same switch as claimant masking — offshore staff on a tenant with Claimant Personal
 * Information Control on — because what the allowlist withholds is the claimant detail rendered
 * into each email. Matches `CaseDetailManager.ShouldHideClaimantPersonalInfoAsync` on the backend.
 */
function allowlistApplies(): boolean {
  return isClientContactMasked()
}

/**
 * True when the correspondence area must be hidden from the current user entirely.
 *
 * With nothing on the allowlist there is nothing an offshore user could ever see, so the whole
 * section is hidden rather than rendered empty. Once a template is approved the section comes back
 * and shows only the records built from approved templates.
 */
export function isCorrespondenceHiddenForCurrentUser(): boolean {
  return allowlistApplies() && allowedTemplateKeys().length === 0
}

/**
 * True when the current user may preview / open the given email template.
 *
 * A missing key counts as not allowed, matching the backend: there is nothing to check it against,
 * and defaulting to "allowed" is what let unlabelled content slip through before.
 */
export function canAccessEmailTemplate(
  templateKey: string | null | undefined
): boolean {
  if (!allowlistApplies()) {
    return true
  }

  if (!templateKey) {
    return false
  }

  return allowedTemplateKeys().some(
    key => key?.toLowerCase() === templateKey.toLowerCase()
  )
}
